<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container apcwo-pr-root">
    <hr-nav v-if="isReviewer" active="mine" :is-hr="isHr" />
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        My Personal Review &amp; Planning
      </oxd-text>
      <oxd-divider />

      <oxd-text v-if="!isLoading && reviews.length === 0" tag="p">
        No review has been assigned to you yet. When HR opens a review cycle
        that includes you, it will appear here.
      </oxd-text>

      <div v-for="review in reviews" :key="review.id" class="apcwo-pr-card">
        <div class="apcwo-pr-card__head">
          <div>
            <div class="apcwo-pr-card__title">{{ review.cycle.name }}</div>
            <div class="apcwo-pr-card__meta">
              Review period {{ formatDate(review.cycle.periodStart) }} –
              {{ formatDate(review.cycle.periodEnd) }}
              <template v-if="review.cycle.dueDate">
                · Due {{ formatDate(review.cycle.dueDate) }}
              </template>
              <template v-if="review.cycle.status === 'closed'">
                · Cycle closed
              </template>
            </div>
          </div>
          <status-badge :status="review.status" />
        </div>

        <div
          v-if="review.unlockReason && review.status === 'draft'"
          class="apcwo-pr-card__note"
        >
          HR reopened this review for changes: {{ review.unlockReason }}
        </div>
        <div v-if="review.submittedAt" class="apcwo-pr-card__meta">
          Submitted {{ review.submittedAt }}
        </div>

        <div class="apcwo-pr-card__actions">
          <oxd-button
            v-if="review.permissions.editEmployee"
            :label="review.status === 'not_started' ? 'Start' : 'Continue'"
            display-type="secondary"
            @click="open(review)"
          />
          <oxd-button
            v-else-if="review.status !== 'not_started'"
            label="View"
            display-type="ghost"
            @click="open(review)"
          />
          <download-buttons
            v-if="review.permissions.downloadEmployee"
            :review-id="review.id"
            scope="employee"
          />
          <template v-if="review.permissions.downloadFinal">
            <download-buttons :review-id="review.id" scope="final" />
            <span class="apcwo-pr-card__meta">final review</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import StatusBadge from '../components/StatusBadge.vue';
import DownloadButtons from '../components/DownloadButtons.vue';
import HrNav from '../components/HrNav.vue';
import {API_BASE, formatDate} from '../util/helpers';

export default {
  name: 'MyReview',
  components: {
    'status-badge': StatusBadge,
    'download-buttons': DownloadButtons,
    'hr-nav': HrNav,
  },
  props: {
    isReviewer: {
      type: Boolean,
      default: false,
    },
    isHr: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `${API_BASE}/reviews`,
    );
    return {http};
  },
  data() {
    return {
      isLoading: true,
      reviews: [],
    };
  },
  created() {
    this.http
      .getAll({mine: true})
      .then((response) => {
        this.reviews = response.data.data;
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    formatDate,
    open(review) {
      navigate('/personalReview/editPersonalReview/{id}', {id: review.id});
    },
  },
};
</script>

<style>
.apcwo-pr-card {
  padding: 16px 18px;
  margin-bottom: 14px;
  border: 1px solid #e3e3ea;
  border-radius: 10px;
}
.apcwo-pr-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.apcwo-pr-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #741e1e;
}
.apcwo-pr-card__meta {
  margin-top: 4px;
  font-size: 13px;
  color: #6b6b7a;
}
.apcwo-pr-card__note {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fff3d6;
  color: #6f4a00;
  font-size: 13px;
}
.apcwo-pr-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}
.apcwo-pr-root {
  font-family: 'Nunito Sans', sans-serif;
}
.apcwo-pr-root button,
.apcwo-pr-root input,
.apcwo-pr-root textarea,
.apcwo-pr-root select {
  font-family: inherit;
}
</style>
